import React, { useEffect, useMemo, useState } from 'react';
import axios from 'config/axios';
import Loader from 'components/Loader';
import { useAuthContext } from 'context/AuthContext';

import FocusAreas from './FocusAreas';
import StudyTracking from './StudyTracking';
import PerformanceHistory from './PerformanceHistory';
import {
    PlanData, TrackingData, TestKind, adaptSat, adaptAct, adaptShsat, adaptTsia2, testTypeParam, fmtDate,
} from './types';
import './index.css';

const SUB_TABS = [
    { id: 'focus', label: 'Latest Snapshot' },
    { id: 'study', label: 'Study Tracking' },
    { id: 'history', label: 'Performance History' },
] as const;

type SubTabId = typeof SUB_TABS[number]['id'];

const KIND_ORDER: TestKind[] = ['sat', 'act', 'shsat', 'tsia2'];
const KIND_LABEL: Record<TestKind, string> = { sat: 'SAT', act: 'ACT', shsat: 'SHSAT', tsia2: 'TSIA2' };
const KIND_FULL_LABEL: Record<TestKind, string> = {
    sat: 'Digital SAT', act: 'Digital ACT', shsat: 'Digital SHSAT', tsia2: 'TSIA2',
};
const EMPTY_PLANS: Record<TestKind, null> = { sat: null, act: null, shsat: null, tsia2: null };

// Mini sparkline of full-test composites (tests with every section scored).
function Sparkline({ totals }: { totals: number[] }) {
    if (totals.length < 2) return null;
    const W = 120, H = 34;
    const min = Math.min(...totals) - 30;
    const max = Math.max(...totals) + 10;
    const x = (i: number) => (W / (totals.length - 1)) * i;
    const y = (v: number) => H - ((v - min) / (max - min)) * H;
    const pts = totals.map((v, i) => `${x(i)},${y(v)}`).join(' ');
    return (
        <svg width={W} height={H} style={{ display: 'block' }}>
            <polyline points={pts} fill='none' stroke='#0575a2' strokeWidth='2' strokeLinejoin='round' strokeLinecap='round' />
            <circle cx={x(totals.length - 1)} cy={y(totals[totals.length - 1])} r='3' fill='#0575a2' />
        </svg>
    );
}

// Compact per-section scores: one slim row of dot-labelled values, so the
// snapshot carries section detail without a full grid.
function SectionScores({ data }: { data: PlanData }) {
    const snap = data.snapshot;
    if (!snap) return null;
    const short = (label: string) => (label === 'Reading & Writing' ? 'R&W' : label);
    // 2×2 grid when there are 4 sections (ACT); single row otherwise.
    const twoRows = data.meta.sections.length > 2;
    return (
        <div className='r-kpi'>
            <div style={twoRows
                ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 22px' }
                : { display: 'flex', gap: 20 }}>
                {data.meta.sections.map(s => {
                    const cell = snap.perSection[s.key];
                    return (
                        <div key={s.key}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#828282' }}>
                                <span style={{ width: 7, height: 7, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                                {short(s.label)}
                            </div>
                            <div style={{ fontSize: 19, fontWeight: 700, color: '#1C2434', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
                                {cell?.score ?? '—'}
                                {cell?.max != null && <span style={{ fontSize: 11, fontWeight: 600, color: '#98a2b3' }}>/{cell.max}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Snapshot({ data }: { data: PlanData }) {
    const { meta, snapshot } = data;

    // Sparkline + deltas come from tests where the metric is defined. The
    // backend nulls `composite` unless all composite sections are scored (ACT
    // excludes Science) and `stem` unless both STEM sections are scored, so
    // filtering on those is exactly right — filtering on ALL sections would
    // wrongly drop ACT tests that omit Science. SHSAT (trendUnit 'percent')
    // plots % correct so the 100-q and 114-q forms are comparable; SAT/ACT plot
    // the raw composite. Deltas follow the series.
    const percentMode = meta.trendUnit === 'percent';
    const series = useMemo(() => {
        if (percentMode) {
            return data.tests
                .map(t => (t.composite != null && t.compositeMax ? Math.round((t.composite / t.compositeMax) * 100) : null))
                .filter((v): v is number => v != null);
        }
        return data.tests.map(t => t.composite).filter((v): v is number => v != null);
    }, [data.tests, percentMode]);
    const n = series.length;
    const deltaLast = n >= 2 ? series[n - 1] - series[n - 2] : null;
    const deltaFirst = n >= 2 ? series[n - 1] - series[0] : null;

    // STEM (ACT) deltas, shown when there are ≥2 tests with STEM defined.
    const stemSeries = useMemo(
        () => (meta.hasStem
            ? data.tests.map(t => t.stem).filter((v): v is number => v != null)
            : []),
        [data.tests, meta.hasStem],
    );
    const stemN = stemSeries.length;
    const stemDeltaLast = stemN >= 2 ? stemSeries[stemN - 1] - stemSeries[stemN - 2] : null;
    const stemDeltaFirst = stemN >= 2 ? stemSeries[stemN - 1] - stemSeries[0] : null;

    const kindLabel = KIND_FULL_LABEL[meta.testKind] ?? 'Digital SAT';
    const isGap = meta.leverageMode === 'accuracy-gap';

    // SAT: when the two section scores come from different tests, note the dates.
    const rw = snapshot?.perSection?.rw;
    const math = snapshot?.perSection?.math;
    const sectionsSubtitle = meta.testKind === 'sat' && rw?.date && math?.date && rw.date !== math.date
        ? `R&W from ${fmtDate(rw.date)} · Math from ${fmtDate(math.date)}`
        : null;

    return (
        <div className='r-snap'>
            <div className='r-snap__ctx'>
                <div className='r-snap__eyebrow'>Current Standing</div>
                <div className='r-snap__name'>{data.student.name}</div>
                <div className='r-snap__sub'>
                    {snapshot
                        ? `${kindLabel} · ${data.tests.length} practice ${data.tests.length === 1 ? 'test' : 'tests'} on record`
                        : `No ${kindLabel} tests on record yet`}
                </div>
            </div>
            {snapshot && (
                <div className='r-kpi'>
                    <div>
                        <div className='r-kpi__label'>{meta.testKind === 'act' ? meta.compositeLabel : 'Now'}</div>
                        <div className='r-kpi__val'>
                            {snapshot.composite ?? '—'}
                            {percentMode && snapshot.compositeMax
                                ? <span style={{ fontSize: 18, fontWeight: 600, color: '#828282' }}> / {snapshot.compositeMax}</span>
                                : null}
                        </div>
                        {percentMode ? (
                            <>
                                {deltaLast != null && (
                                    <div className='r-kpi__meta' style={{ color: deltaLast >= 0 ? '#00a445' : '#d60606' }}>
                                        {deltaLast >= 0 ? '▲ +' : '▼ '}{deltaLast} pts vs last
                                    </div>
                                )}
                                {deltaFirst != null && (
                                    <div className='r-kpi__meta' style={{ color: deltaFirst >= 0 ? '#00a445' : '#d60606' }}>
                                        {deltaFirst >= 0 ? '▲ +' : '▼ '}{deltaFirst} pts vs first
                                    </div>
                                )}
                            </>
                        ) : meta.testKind === 'act' ? (
                            <>
                                {deltaLast != null && (
                                    <div className='r-kpi__meta' style={{ color: deltaLast >= 0 ? '#00a445' : '#d60606' }}>
                                        {deltaLast >= 0 ? '▲ +' : '▼ '}{deltaLast} vs last
                                    </div>
                                )}
                                {deltaFirst != null && (
                                    <div className='r-kpi__meta' style={{ color: deltaFirst >= 0 ? '#00a445' : '#d60606' }}>
                                        {deltaFirst >= 0 ? '▲ +' : '▼ '}{deltaFirst} vs first
                                    </div>
                                )}
                            </>
                        ) : deltaLast != null ? (
                            <>
                                <div className='r-kpi__meta' style={{ color: deltaLast >= 0 ? '#00a445' : '#d60606' }}>
                                    {deltaLast >= 0 ? '▲ +' : '▼ '}{deltaLast} vs last
                                </div>
                                <div className='r-kpi__meta' style={{ color: deltaFirst! >= 0 ? '#00a445' : '#d60606' }}>
                                    {deltaFirst! >= 0 ? '▲ +' : '▼ '}{deltaFirst} vs first
                                </div>
                            </>
                        ) : sectionsSubtitle ? (
                            <div className='r-kpi__meta'>{sectionsSubtitle}</div>
                        ) : null}
                    </div>
                    {meta.testKind !== 'act' && <Sparkline totals={series} />}
                </div>
            )}
            {snapshot && meta.hasStem && (
                <div className='r-kpi'>
                    <div>
                        <div className='r-kpi__label'>STEM</div>
                        <div className='r-kpi__val'>{snapshot.stem ?? '—'}</div>
                        {stemDeltaLast != null && (
                            <div className='r-kpi__meta' style={{ color: stemDeltaLast >= 0 ? '#00a445' : '#d60606' }}>
                                {stemDeltaLast >= 0 ? '▲ +' : '▼ '}{stemDeltaLast} vs last
                            </div>
                        )}
                        {stemDeltaFirst != null && (
                            <div className='r-kpi__meta' style={{ color: stemDeltaFirst >= 0 ? '#00a445' : '#d60606' }}>
                                {stemDeltaFirst >= 0 ? '▲ +' : '▼ '}{stemDeltaFirst} vs first
                            </div>
                        )}
                    </div>
                </div>
            )}
            {snapshot && <SectionScores data={data} />}
            {snapshot && (
                <div className='r-kpi'>
                    <div>
                        <div className='r-kpi__label'>
                            {isGap ? <><span style={{ textTransform: 'none' }}>Qs</span> on Table</> : 'Pts on Table'}
                        </div>
                        <div className='r-kpi__val r-kpi__val--up'>
                            {isGap ? `~${Math.round(snapshot.leverageTotal)}` : `+${snapshot.leverageTotal}`}
                            {isGap && <span style={{ fontSize: 14, fontWeight: 600, color: '#828282' }}> /test</span>}
                        </div>
                        <div className='r-kpi__meta'>
                            {`across top ${data.top.length} ${meta.testKind === 'sat'
                                ? (data.top.length === 1 ? 'skill' : 'skills')
                                : (data.top.length === 1 ? 'topic' : 'topics')}`}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PlanEmptyState() {
    return (
        <div className='r-card r-emptycard'>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1C2434', marginBottom: 6 }}>
                Available for Digital SAT, ACT, SHSAT &amp; TSIA2 students
            </div>
            This view is built from completed Digital SAT, ACT, SHSAT, or TSIA2 practice tests. Once this
            student completes one, their focus areas and score history will appear here.
        </div>
    );
}

const latestDate = (p: PlanData | null): string =>
    p && p.tests.length ? p.tests[p.tests.length - 1].date : '';

function PersonalizedPlan({ studentId }: { studentId: string }) {
    const [plans, setPlans] = useState<Record<TestKind, PlanData | null>>({ ...EMPTY_PLANS });
    const [loading, setLoading] = useState(true);
    const [planError, setPlanError] = useState(false);
    const [activeKind, setActiveKind] = useState<TestKind>('sat');

    const [tracking, setTracking] = useState<Record<TestKind, TrackingData | null>>({ ...EMPTY_PLANS });
    const [trackingError, setTrackingError] = useState(false);
    const [subTab, setSubTab] = useState<SubTabId>('focus');

    const { user } = useAuthContext();
    const hideVocab = !!user?.customizations?.find((c: string) => c === 'Hide Vocab');

    useEffect(() => {
        setLoading(true);
        setPlanError(false);
        setPlans({ ...EMPTY_PLANS });
        setTracking({ ...EMPTY_PLANS });
        Promise.allSettled([
            axios.get(`tutors/digital/sat/personalizedPlan/${studentId}/`),
            axios.get(`tutors/digital/act/personalizedPlan/${studentId}/`),
            axios.get(`tutors/digital/shsat/personalizedPlan/${studentId}/`),
            axios.get(`tutors/digital/tsia2/personalizedPlan/${studentId}/`),
        ]).then(([satR, actR, shsatR, tsia2R]) => {
            const next: Record<TestKind, PlanData | null> = {
                sat: satR.status === 'fulfilled' ? adaptSat(satR.value.data) : null,
                act: actR.status === 'fulfilled' ? adaptAct(actR.value.data) : null,
                shsat: shsatR.status === 'fulfilled' ? adaptShsat(shsatR.value.data) : null,
                tsia2: tsia2R.status === 'fulfilled' ? adaptTsia2(tsia2R.value.data) : null,
            };
            if (!KIND_ORDER.some(k => next[k])) { setPlanError(true); setLoading(false); return; }
            setPlans(next);
            // Default to whichever kind has data; if several, the most recently tested.
            const withData = KIND_ORDER.filter(k => next[k]?.snapshot);
            if (withData.length) {
                withData.sort((a, b) => latestDate(next[b]) > latestDate(next[a]) ? 1 : -1);
                setActiveKind(withData[0]);
            } else {
                setActiveKind('sat');
            }
            setLoading(false);
        });
    }, [studentId]);

    const activePlan = plans[activeKind];

    // Study Tracking loads lazily per active kind, the first time it's opened.
    useEffect(() => {
        if (subTab !== 'study' || !activePlan || tracking[activeKind]) return;
        setTrackingError(false);
        axios.get(`tutors/students/${studentId}/studyTracking/?testType=${encodeURIComponent(testTypeParam(activeKind))}`)
            .then(res => setTracking(prev => ({ ...prev, [activeKind]: res.data })))
            .catch(error => { console.log(error); setTrackingError(true); });
    }, [subTab, studentId, activeKind, activePlan, tracking]);

    if (loading) return <Loader />;
    if (planError || !activePlan) {
        return <div className='r-card r-emptycard'>Couldn't load the personalized plan. Try refreshing.</div>;
    }

    const availableKinds = KIND_ORDER.filter(k => plans[k]?.snapshot);
    const hasData = activePlan.snapshot != null;
    const activeTracking = tracking[activeKind];

    return (
        <div className='r-wrap'>
            <Snapshot data={activePlan} />
            <div className='r-tabrow'>
                <div className='r-pills'>
                    {SUB_TABS.map(t => (
                        <button
                            key={t.id}
                            className={subTab === t.id ? 'r-pill r-pill--on' : 'r-pill'}
                            onClick={() => setSubTab(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                {availableKinds.length > 1 && (
                    <div className='r-seg' role='group' aria-label='Test type'>
                        {availableKinds.map(k => (
                            <button
                                key={k}
                                className={activeKind === k ? 'r-seg__btn r-seg__btn--on' : 'r-seg__btn'}
                                onClick={() => setActiveKind(k)}
                            >
                                {KIND_LABEL[k]}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {subTab === 'focus' && (hasData ? <FocusAreas key={activeKind} data={activePlan} /> : <PlanEmptyState />)}
            {subTab === 'history' && (hasData ? <PerformanceHistory key={activeKind} data={activePlan} /> : <PlanEmptyState />)}
            {subTab === 'study' && (
                trackingError
                    ? <div className='r-card r-emptycard'>Couldn't load study tracking. Try refreshing.</div>
                    : activeTracking
                    ? <StudyTracking key={activeKind} data={activeTracking} hideVocab={hideVocab} testKind={activeKind} />
                    : <Loader />
            )}
        </div>
    );
}

export default PersonalizedPlan;
